const { normalize, schema: S } = require('normalizr');
const { assert } = require('../utils/assert');
const IsEmpty = require('lodash/isEmpty');
const { normalizeSmsPreference } = require('utils/smsPreference');
const OmitBy = require('lodash/omitBy');
const IsUndefined = require('lodash/isUndefined');
const IsNull = require('lodash/isNull');
const Omit = require('lodash/omit');
const Remove = require('lodash/remove');
const Union = require('lodash/union');
const Pick = require('lodash/pick');
const { safeWarning } = require('assertions-simplified');
const Unary = require('lodash/unary');
const First = require('lodash/first');
const IsEmail = require('utils/is-email');
const Moment = require('moment');
const { getSSOProvider } = require('utils/sso');
const AuthConstants = require('services/auth-management/constants');

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

const internals = {};

module.exports = (context) => {

    const http = context.http.nearpeer;
    const rootHttp = context.http.nearpeerRoot || http;
    const selectors = context.selectors.all;
    const redux = context.redux.hooks;
    const configuration = context.configuration.twilioApi;
    const getToken = () => selectors.getApiToken(redux.getState());
    const authorization = (token) => ({ headers: { Authorization: `Bearer ${token || getToken()}` } });
    const userIsAuthenticated = () => selectors.getIsAuthenticated(redux.getState());
    const retryable = (options = {}) => ({
        ...options,
        serviceUnavailableRetry: true
    });
    const retryableGet = (url, options) => http.get(url, retryable(options));

    const schema = {
        School: new S.Entity('schools'),
        SchoolExtension: new S.Entity('schoolExtensions'),
        ExtendedSchool: new S.Object({}),
        Class: new S.Entity('classes'),
        ClassWithUsers: new S.Object({}),
        Survey: new S.Entity('surveys'),
        Interest: new S.Entity('interests'),
        RoleGroup: new S.Entity('roleGroups'),
        Role: new S.Entity('roles'),
        Category: new S.Entity('categories'),
        BadgeType: new S.Entity('badgeTypes'),
        ConversationStarter: new S.Entity('conversationStarters'),
        YearHired: new S.Entity('yearsHired'),
        Office: new S.Entity('offices'),
        Housing: new S.Entity('housing'),
        Major: new S.Entity('majors'),
        Department: new S.Entity('departments'),
        User: new S.Entity('users'),
        UserExtension: new S.Entity('userExtensions'),
        UserSimilarity: new S.Entity('userSimilarities'),
        Notification: new S.Entity('notifications'),
        ExtendedUser: new S.Object({}),
        ExtendedUserWithSimilarities: new S.Object({}),
        SearchResultUser: new S.Object({}),
        CurrentUser: new S.Object({}),
        ChatRequest: new S.Object({}),
        Transfer: new S.Entity('transfers'),
        TransferExtension: new S.Entity('transferExtensions'),
        SurveyQuestions: new S.Entity('surveyQuestions'),
        QuestionAnswers: new S.Entity('questionAnswers'),
        ExtendedTransfer: new S.Object({}),
        AnnouncementConversation: new S.Entity('announcementConversations'),
        AnnouncementMessage: new S.Entity('announcementMessages'),
        AnnouncementUpdateDates: new S.Object({}),
        ChannelLastMessage: new S.Object({}),
        AnnouncementCounts: new S.Object({}),
        Offer: new S.Entity('offers'),
        Resource: new S.Entity('resources')
    };

    schema.User.define({
        majors: [schema.Major],
        department: schema.Department,
        office: schema.Office,
        yearHired: schema.YearHired
    });

    schema.SchoolExtension.define({
        transfers: [schema.Transfer],
        housing: [schema.Housing],
        majors: [schema.Major],
        departments: [schema.Department],
        interests: [schema.Interest],
        categories: [schema.Category],
        surveys: [schema.Survey]
    });

    schema.Class.define({
        conversation: schema.AnnouncementConversation,
        roles: [schema.Role]
    });

    schema.ExtendedSchool.define({
        school: schema.School,
        extension: schema.SchoolExtension
    });

    schema.UserSimilarity.define({
        interests: [schema.Interest],
        passionInterests: [schema.Interest],
        classes: [schema.Class],
        majors: [schema.Major],
        relatedMatches: [String],
        similarityDebug: new S.Object({})
    });

    schema.SearchResultUser.define({
        user: schema.User,
        similarityDetails: schema.UserSimilarity
    });

    schema.UserExtension.define({
        peers: [schema.User],
        interests: [schema.Interest],
        passionInterests: [schema.Interest],
        school: schema.School,
        classes: [schema.Class],
        role: [schema.Role],
        surveys: [schema.Survey],
        housing: new S.Union({ // Is a housing schema when id is present
            ref: schema.Housing
        }, (h) => h.id && 'ref'),
        transfer: new S.Union({ // Is a transfer schema when id is present
            ref: schema.Transfer
        }, (t) => t.id && 'ref')
    });

    schema.ExtendedUser.define({
        user: schema.User,
        extension: schema.UserExtension
    });

    schema.ExtendedUserWithSimilarities.define({
        user: schema.User,
        extension: schema.UserExtension,
        similarityDetails: schema.UserSimilarity
    });

    schema.ClassWithUsers.define({
        class: schema.Class,
        users: [schema.User]
    });

    schema.ChatRequest.define({
        user: schema.User
    });

    schema.CurrentUser.define({
        user: schema.User,
        extension: schema.UserExtension,
        currentUserDetails: {
            peersIn: [schema.User],
            peersOut: [schema.User],
            peersDeclined: [schema.User],
            peersDeclinedBy: [schema.User],
            chatRequestsIn: [schema.ChatRequest],
            chatRequestsOut: [schema.ChatRequest]
        }
    });

    schema.TransferExtension.define({
        class: schema.Class
    });

    schema.ExtendedTransfer.define({
        transfer: schema.Transfer,
        extension: schema.TransferExtension
    });

    schema.Notification.define({
        sender: schema.User,
        transfer: schema.ExtendedTransfer,
        class: schema.Class
    });

    schema.AnnouncementMessage.define({
        conversation: schema.AnnouncementConversation
    });

    schema.Survey.define({
        questions: [schema.SurveyQuestions]
    });

    schema.SurveyQuestions.define({
        answers: [schema.QuestionAnswers]
    });

    const announcementMessageEntities = {};
    const announcementMessageEntities_set = (messages) => {

        [].concat(messages).forEach((m) => {

            announcementMessageEntities[m.id] = m;
        });

        return messages;
    };

    // const announcementMessageEntities_get = (id) => {

    //     const m = announcementMessageEntities[id] || null;

    //     if (m === null) {
    //         safeWarning(`Haven't seen message ${id} yet!`);
    //     }

    //     return m;
    // };

    // const announcementMessageEntities_del = (id) => delete announcementMessageEntities[id];

    const isPrivateChannel = (channel) => {

        return channel
            && (isValid_privateDMChannelIdentifier(channel.uniqueName)
                || isValid_privateClassChannelIdentifier(channel.uniqueName));
    };

    const isValid_privateDMChannelIdentifier = (channelIdentifier) => {

        return channelIdentifier
            && channelIdentifier.split('-').length === 3
            && channelIdentifier.split('-')[0] === configuration.environmentPrefix
            && channelIdentifier.split('-')[1] === 'nearpeer'
            && channelIdentifier.split('-')[2].split(',').length === 2;
    };

    const isValid_privateClassChannelIdentifier = (channelIdentifier) => {

        return channelIdentifier
            && channelIdentifier.split('-').length === 4
            && channelIdentifier.split('-')[0] === configuration.environmentPrefix
            && channelIdentifier.split('-')[1] === 'nearpeer'
            && channelIdentifier.split('-')[2] === 'class'
            && channelIdentifier.split('-')[3];
    };

    const isValid_userIdentifier = (userIdentifier) => {

        return userIdentifier
            && userIdentifier.split('-').length === 3
            && userIdentifier.split('-')[0] === configuration.environmentPrefix
            && userIdentifier.split('-')[1] === 'nearpeer';
    };

    const extractUserIds_fromPrivateChannelIdentifier = (channelIdentifier) => {

        if (!isValid_privateDMChannelIdentifier(channelIdentifier)) {
            safeWarning(`Invalid DM channel type! Unique name: ${channelIdentifier}`);
            return [];
        }

        return channelIdentifier.split('-')[2].split(',').map(Unary(parseInt));
    };

    const extractClassId_fromPrivateChannelIdentifier = (channelIdentifier) => {

        if (!isValid_privateClassChannelIdentifier(channelIdentifier)) {
            safeWarning(`Invalid class channel type! Unique name: ${channelIdentifier}`);
            return null;
        }

        return parseInt(channelIdentifier.split('-')[3]);
    };

    const extractUserId_fromUserIdentifier = (userIdentifier) => {

        if (!isValid_userIdentifier(userIdentifier)) {
            safeWarning(`Invalid user identifier! Identifier: ${userIdentifier}`);
            return null;
        }

        return First([userIdentifier.split('-')[2]].map(Unary(parseInt)));
    };

    const toDomain = {
        announcementConversations: (conversations) => conversations.filter(isPrivateChannel).map(toDomain.announcementConversation),
        announcementConversation: (conversation) => {

            const type = isValid_privateDMChannelIdentifier(conversation.uniqueName) ? 'dm' :
                isValid_privateClassChannelIdentifier(conversation.uniqueName) ? 'class' : null;

            if (type === null) {
                safeWarning(`Invalid channel identifier! Identifier: ${conversation.uniqueName}`);
            }

            return {
                id: conversation.id,
                type,
                userIds: (type === 'dm') ? extractUserIds_fromPrivateChannelIdentifier(conversation.uniqueName) : null,
                classId: (type === 'class') ? extractClassId_fromPrivateChannelIdentifier(conversation.uniqueName) : null,
                lastMessage: conversation.lastMessage ? {
                    author: conversation.lastMessage.author,
                    body: conversation.lastMessage.body,
                    timestamp: conversation.lastMessage.dateCreated
                } : null
            };
        },
        announcementMessages: (messages) => messages.map(toDomain.announcementMessage),
        announcementMessage: (message) => {

            return {
                id: message.id,
                conversation: toDomain.announcementConversation(message.conversation),
                author: extractUserId_fromUserIdentifier(message.author),
                body: message.body,
                timestamp: message.dateCreated,
                index: message.index,
                classId: message.conversation && message.conversation.classId,
                moderationStatus: message.attributes && message.attributes.moderationStatus || 'ok',
                isPinned: message.attributes && message.attributes.isPinned || false,
                isEdited: message.attributes && message.attributes.isEdited || false
            };
        }
    };

    const userExtensionFields = [
        'peers',
        'interests',
        'passionInterests',
        'school',
        'housing',
        'classes',
        'transfer',
        'surveys'
    ];

    const currentUserFields = [
        'peersIn',
        'peersOut',
        'peersDeclined',
        'peersDeclinedBy',
        'chatRequestsIn',
        'chatRequestsOut'
    ];

    const fixExtendedUser = (user) => ({
        user: Omit(user, Union(userExtensionFields, currentUserFields)),
        extension: Object.assign({ id: user.id }, Pick(user, userExtensionFields))
    });
    const fixCurrentUser = (user) => ({
        user: Omit(user, Union(userExtensionFields, currentUserFields)),
        extension: Object.assign({ id: user.id }, Pick(user, userExtensionFields)),
        currentUserDetails: (() => {

            return Object.assign({ id: user.id }, Pick(user, currentUserFields));
        })()
    });

    const schoolExtensionFields = [
        'housing',
        'transfers',
        'majors',
        'departments',
        'categories',
        'interests',
        'appContextOptions',
        'roles',
        'usedRoleIds',
        'surveys',
        'isCompany',
        'isCommunity',
        'isOnline',
        'usePasswordRules'
    ];

    const fixExtendedSchool = (school) => ({
        school: Omit(school, schoolExtensionFields),
        extension: {
            id: school.id,
            ...Pick(school, schoolExtensionFields)
        }
    });

    const fixUserSearchResults = (users) => {

        return users.map(({ similarityDetails, ...user }) => ({
            user,
            similarityDetails: {
                id: user.id,
                ...similarityDetails
            }
        }));
    };

    const fixExtendedUserWithSimilarities = ({ similarityDetails, ...user }) => {

        const extendedUser = fixExtendedUser(user);

        extendedUser.user.detailsFetchedAt = Date.now();

        return {
            ...extendedUser,
            similarityDetails: {
                id: user.id,
                ...similarityDetails
            }
        };
    };

    // const fixNotifyUserSearchResults = (users) => {

    //     return users.map((user) => ({
    //         user
    //     }));
    // };

    const fixExtendedTransfer = (transfer) => ({
        transfer: Omit(transfer, ['class']),
        extension: Object.assign({ id: transfer.id }, Pick(transfer, ['class']))
    });

    const fixNotification = (n) => ({
        ...n,
        transfer: n.transfer && fixExtendedTransfer(n.transfer)
    });

    const prepareSignupDetails = (signupDetails) => {

        // Turn birthdate into ISO string
        if (signupDetails.birthdate && typeof signupDetails.birthdate.toISOString === 'function') {
            signupDetails.birthdate = signupDetails.birthdate.toISOString();
        }

        // This is for signupDetails.major since it's cast to an array
        // but may have been null

        const isNullArray = (item) => {

            if (Array.isArray(item)) {
                Remove(item, IsNull); // NOTE mutates item
                return !item.length;
            }

            return false;
        };

        // Awww yeah

        const omitByAll = (obj, filterFuncs) => filterFuncs.reduce(OmitBy, obj);
        const filteredSignupDetails = omitByAll(signupDetails, [isNullArray, IsUndefined, IsNull]);

        return filteredSignupDetails;
    };

    return {
        communityLaunch: {
            founder: (id) => http.get(`/community-launch/profiles/${id}/founder`, authorization()).then((response) => response.data),
            status: () => http.get('/community-launch', authorization()).then((response) => response.data),
            referrals: () => http.get('/community-launch/referrals', authorization()).then((response) => response.data),
            invite: (payload) => http.post('/community-launch/referrals', payload, authorization()).then((response) => response.data)
        },
        gamification: {
            decorations: () => http.get('/gamification/decorations', authorization()).then((response) => response.data),
            summary: () => http.get('/gamification', authorization()).then((response) => response.data),
            profile: (id) => http.get(`/gamification/profiles/${id}`, authorization()).then((response) => response.data),
            leaderboard: (period) => http.get('/gamification/leaderboard', { ...authorization(), params: { period } }).then((response) => response.data),
            participation: (optedOut) => http.post('/gamification/participation', { optedOut }, authorization()).then((response) => response.data),
            prizes: () => http.get('/gamification/prizes', authorization()).then((response) => response.data),
            admin: () => http.get('/gamification/admin', authorization()).then((response) => response.data),
            savePrize: (payload) => http.put('/gamification/admin/prize', payload, authorization()).then((response) => response.data),
            savePrizeContacts: (recipientEmails) => http.put('/gamification/admin/contacts', { recipientEmails }, authorization()).then((response) => response.data),
            prizeWinners: (id) => http.get(`/gamification/admin/prizes/${id}/winners`, authorization()).then((response) => response.data),
            uploadPrizeImage: (payload) => http.post('/gamification/admin/upload-image', internals.form(payload), authorization()).then((response) => response.data),
            referrals: () => http.get('/gamification/referrals', authorization()).then((response) => response.data),
            invite: (payload) => http.post('/gamification/referrals', payload, authorization()).then((response) => response.data)
        },
        nerpy: {
            peerGames: (sid, before) => http.get(`/assistant/nerpy/peer-games/${sid}`, { ...authorization(), timeout: 15000, ...(before ? { params: { before } } : {}) }).then(({ data }) => data),
            invitePeerGame: (sid, payload) => http.post(`/assistant/nerpy/peer-games/${sid}/invitations`, payload, { ...authorization(), timeout: 15000 }).then(({ data }) => data),
            peerGameAction: (sid, id, payload) => http.post(`/assistant/nerpy/peer-games/${sid}/sessions/${id}`, payload, { ...authorization(), timeout: 15000 }).then(({ data }) => data),
            profile: () => http.get('/assistant/nerpy/profile', authorization()).then(({ data }) => data),
            triviaCatalog: () => http.get('/assistant/nerpy/trivia/catalog', authorization()).then(({ data }) => data),
            triviaGames: () => http.get('/assistant/nerpy/trivia/games', authorization()).then(({ data }) => data),
            triviaGamesAdmin: () => http.get('/assistant/nerpy/trivia/games/admin', authorization()).then(({ data }) => data),
            saveTriviaGame: (game) => http.put('/assistant/nerpy/trivia/games/admin', game, authorization()).then(({ data }) => data),
            saveTriviaSchedule: (schedule) => http.put('/assistant/nerpy/trivia/games/schedule', schedule, authorization()).then(({ data }) => data),
            saveTriviaQuestion: (question) => http.put('/assistant/nerpy/trivia/catalog', question, authorization()).then(({ data }) => data),
            triviaNext: (payload) => http.post('/assistant/nerpy/trivia/next', payload, authorization()).then(({ data }) => data),
            triviaAction: (id, payload) => http.post(`/assistant/nerpy/trivia/rounds/${id}`, payload, authorization()).then(({ data }) => data),
            preferences: () => http.get('/assistant/nerpy/preferences', authorization()).then(({ data }) => data),
            savePreferences: (preferences) => http.put('/assistant/nerpy/preferences', preferences, authorization()).then(({ data }) => data),
            cancelOutreach: (id) => http.post('/assistant/nerpy/outreach/cancel', { id }, authorization()),
            outreachStatus: () => http.get('/assistant/nerpy/outreach/status', authorization()).then(({ data }) => data),
            outreachSettings: () => http.get('/assistant/nerpy/outreach/settings', authorization()).then(({ data }) => data),
            saveOutreachSettings: (settings) => http.put('/assistant/nerpy/outreach/settings', settings, authorization()).then(({ data }) => data),
            answerCheckIn: (id, answer) => http.post('/assistant/nerpy/outreach/answer', { id, answer }, authorization()).then(({ data }) => data),
            pauseOutreach: (paused) => http.post('/assistant/nerpy/outreach/pause', { paused }, authorization()).then(({ data }) => data),
            availability: () => http.get('/assistant/nerpy', authorization()).then(({ data }) => data),
            message: (text, requestId) => http.post('/assistant/nerpy/messages', { text, ...(requestId ? { requestId } : {}) }, authorization()).then(({ data }) => data),
            conversation: () => http.get('/assistant/nerpy/conversation', authorization()).then(({ data }) => data),
            read: () => http.post('/assistant/nerpy/read', {}, authorization())
        },
        ping() {

            return retryableGet('/ping').then(
                (response) => response
            );
        },
        checkIp() {

            return retryableGet('/ip-check/country-code').then(
                (response) => response
            );
        },
/*
@np-doc
id: frontend.docs.session-api
title: Frontend docs session API call
section: Platform
order: 10
summary: Calls the API root /docs/session route with bearer auth and credentials so the API can set the docs cookie.
tags: docs,auth,api
*/
        docs: {
            // #region docs-create-session
            createSession() {

                return rootHttp.post('/docs/session', undefined, {
                    ...authorization(),
                    withCredentials: true
                }).then(
                    (response) => response.data
                );
            }
            // #endregion docs-create-session
        },
/* @np-doc-end */
        supportDebug: {
            async resolveDebugSession({ code, platform, appVersion, deviceId, originalUrl }) {

                const response = await http.post('/support/debug-sessions/resolve', {
                    code,
                    platform,
                    appVersion,
                    deviceId,
                    originalUrl
                });

                return response.data.session;
            },
            async submitDebugReport({ debugSessionId, code, audience, note, platform, appVersion, route, userAgent, report, metadata }) {

                const maybeAuthorization = userIsAuthenticated() ? authorization() : {};
                const response = await http.post('/support/debug-reports', {
                    debugSessionId,
                    code,
                    audience,
                    note,
                    platform,
                    appVersion,
                    route,
                    userAgent,
                    report,
                    metadata
                }, maybeAuthorization);

                return response.data;
            },
            async reportClientError({ platform, appVersion, route, userAgent, report }) {

                let maybeAuthorization = {};

                try {
                    maybeAuthorization = userIsAuthenticated() ? authorization() : {};
                }
                catch (err) {
                    // Startup failures can occur before Redux authentication is available.
                }

                const response = await http.post('/support/client-errors', {
                    platform,
                    appVersion,
                    route,
                    userAgent,
                    report
                }, {
                    ...maybeAuthorization,
                    timeout: 2000
                });

                return response.data;
            }
        },
        authentication: {
            async login({ email, password, token }) {

                // email and pass but no token, or no email or password but with a token
                assert((IsUndefined(email) && IsUndefined(password) && !IsUndefined(token)) ||
                    (!IsUndefined(email) && !IsUndefined(password) && IsUndefined(token)));

                if (!IsUndefined(token) && !token) {
                    const error = new Error('No login token on init');
                    error.code = 'NO_TOKEN_ON_INIT';
                    return Promise.reject(error);
                }

                const maybeAuthorization = !IsUndefined(token) ? authorization(token) : {};

                const response = await http.post(
                    '/login',
                    { email, password },
                    maybeAuthorization
                );

                if (internals.isPendingMfaStatus(response.data?.status)) {
                    return response.data;
                }

                const signupContext = internals.buildSignupContext(response.data, {
                    fallbackEmail: email
                });

                return {
                    tokens: {
                        nearpeer: response.data.nearpeer,
                        twilio: response.data.twilio
                    },
                    isVerified: response.data.isVerified,
                    schoolId: response.data.schoolId,
                    verificationToken: signupContext.verificationToken,
                    roleId: signupContext.roleId,
                    email: signupContext.email,
                    signupContext
                };
            },
            loginWithVerificationToken({ verificationToken }) {

                return http.post('/login/verification-token', { verificationToken }, {}).then(
                    (response) => internals.buildVerificationTokenAuthResult(response.data)
                );
            },

            loginSSO({ token, sso }) {

                if (!sso) {
                    throw new Error('Missing SSO provider');
                }

                const ssoProvider = getSSOProvider(sso);

                if (ssoProvider === 'auth0') {
                    return http.post('/login/auth0', { token }, {}).then(
                        (response) => {

                            if (internals.isPendingMfaStatus(response.data?.status)) {
                                return response.data;
                            }

                            const {
                                sso,
                                nearpeer,
                                twilio,
                                isVerified,
                                verificationToken,
                                roleId
                            } = response.data;

                            const signupContext = internals.buildSignupContext(response.data);

                            return {
                                tokens: {
                                    sso,
                                    nearpeer,
                                    twilio
                                },
                                isVerified,
                                schoolId: response.data.schoolId,
                                verificationToken,
                                roleId,
                                email: signupContext.email,
                                signupContext
                            };
                        }
                    );
                }

                return http.post('/login/keycloak', { token }, {}).then(
                    (response) => {

                        if (internals.isPendingMfaStatus(response.data?.status)) {
                            return response.data;
                        }

                        const {
                            sso,
                            nearpeer,
                            twilio,
                            isVerified,
                            verificationToken,
                            roleId
                        } = response.data;

                        const signupContext = internals.buildSignupContext(response.data);

                        return {
                            tokens: {
                                sso,
                                nearpeer,
                                twilio
                            },
                            isVerified,
                            schoolId: response.data.schoolId,
                            verificationToken,
                            roleId,
                            email: signupContext.email,
                            signupContext
                        };
                    }
                );
            },
            startMfaSetup({ mfaToken, phone, phoneCountry }) {

                const maybeAuthorization = userIsAuthenticated() ? authorization() : {};

                return http.post('/login/mfa/start', {
                    mfaToken,
                    phone,
                    phoneCountry
                }, maybeAuthorization)
                    .then((response) => response.data);
            },
            resendMfaChallenge({ mfaToken }) {

                const maybeAuthorization = userIsAuthenticated() ? authorization() : {};

                return http.post('/login/mfa/resend', { mfaToken }, maybeAuthorization)
                    .then((response) => response.data);
            },
            resetMfaPhone({ mfaToken }) {

                const maybeAuthorization = userIsAuthenticated() ? authorization() : {};

                return http.post('/login/mfa/reset-phone', { mfaToken }, maybeAuthorization)
                    .then((response) => response.data);
            },
            verifyMfa({ mfaToken, code }) {

                const maybeAuthorization = userIsAuthenticated() ? authorization() : {};

                return http.post('/login/mfa/verify', { mfaToken, code }, maybeAuthorization).then(
                    (response) => {

                        if (response.data?.status === 'verified') {
                            return response.data;
                        }

                        const signupContext = internals.buildSignupContext(response.data);

                        return {
                            tokens: {
                                nearpeer: response.data.nearpeer,
                                twilio: response.data.twilio
                            },
                            isVerified: response.data.isVerified,
                            schoolId: response.data.schoolId,
                            verificationToken: signupContext.verificationToken,
                            roleId: signupContext.roleId,
                            email: signupContext.email,
                            signupContext
                        };
                    }
                );
            },
            logout() {

                return http.post('/logout', undefined, authorization()).then(
                    (response) => undefined
                );
            },
            closeAccount(closeAccountReason) {

                return http.post('/users/close-account', { closeAccountReason }, authorization()).then(
                    (response) => undefined
                );
            },
            requestPasswordReset({ email }) {

                return http.post('/users/request-reset', { email }).then(
                    (response) => response
                );
            },
            resetPassword({ password: newPassword, resetToken }) {

                return http.post('/users/reset-password', { newPassword, resetToken }).then(
                    (response) => response
                );
            },
            checkResetToken({ resetToken }) {

                return http.post('/users/reset-token/check', { resetToken }, retryable()).then(
                    (response) => response
                );
            },
            updateTwilioToken() {

                return retryableGet('/users/chat-token', authorization()).then(
                    (response) => ({ token: response.data })
                );
            },
            storeFirebaseToken({ bindingType, token }) {

                const payload = { bindingType, token };

                return http.post('/user/firebase-token', payload, authorization()).then(
                    (response) => response.data
                );
            },
            deleteTwilioBinding({ endpoint }) {

                const options = {
                    ...authorization(),
                    data: { endpoint }
                };

                return http.delete('/user/twilio-binding', options).then(
                    (response) => response.data
                );
            },
            deleteFirebaseToken({ token }) {

                const options = {
                    ...authorization(),
                    data: { token }
                };

                return http.delete('/user/firebase-token', options).then(
                    (response) => response.data
                );
            }
        },
        signup: {
            startSignup({ email, schoolId, signupIntent }) {

                return http.post('/users', {
                    email,
                    schoolId,
                    ...(signupIntent ? { signupIntent } : {}),
                    signupSource: window.location.pathname
                }).then(
                    (response) => response
                );
            },
            async checkPassword({ verificationToken }) {
                return retryableGet(`/users/signup-progress/${verificationToken}/password/check`)
                    .then((response) => response);
            },
            checkSchoolSSO({ email, schoolId }) {

                return retryableGet(internals.getSchoolCheckUrl({ email, schoolId })).then(
                    (response) => response.data
                );
            },
            storePassword({ verificationToken, password }) {

                return http.patch(`/users/signup-progress/${verificationToken}/password`, {
                    password
                }).then(
                    (response) => undefined
                );
            },
            resendEmail({ email, schoolId }) {

                return http.post('/users/resend', { email, schoolId }).then(
                    (response) => undefined
                );
            },
            verifyEmail({ verificationToken }) {

                return http.get(`/users/verify/${verificationToken}`).then(
                    (response) => undefined
                );
            },
            completeSignup(signupDetails) {

                const preparedDetails = prepareSignupDetails(signupDetails);
                return http.patch('/users/complete-profile', internals.form(preparedDetails))
                    .then((response) => internals.buildCompletedSignupAuthResult(response.data));
            },
            fetchSignupProgress({ verificationToken }) {

                return retryableGet(`/users/signup-progress/${verificationToken}`)
                    .then(
                        (response) => response.data
                    );
            },
            updateSignupProgress({ verificationToken, signupDetails }) {

                if (signupDetails.birthdate && typeof signupDetails.birthdate.toISOString === 'function') {
                    signupDetails.birthdate = signupDetails.birthdate.toISOString();
                }

                const preparedSignupDetails = Object.keys(signupDetails).reduce((collector, key) => {

                    const val = signupDetails[key];

                    if (val === null) {
                        collector[key] = '';
                    }
                    else {
                        collector[key] = val;
                    }

                    return collector;
                }, {});

                return http.patch(`/users/signup-progress/${verificationToken}`, internals.form(preparedSignupDetails))
                    .then(
                        (response) => undefined
                    );
            },
            updateSignupProgressProfilePic({ verificationToken, pictureDetails }) {

                const preparedPictureDetails = Object.keys(pictureDetails).reduce((collector, key) => {

                    const val = pictureDetails[key];

                    if (val === null) {
                        collector[key] = '';
                    }
                    else {
                        collector[key] = val;
                    }

                    return collector;
                }, {});

                return http.patch(`/users/signup-progress/${verificationToken}/profile-picture`, internals.form(preparedPictureDetails))
                    .then(
                        (response) => undefined
                    );
            },
            updateUserPreferencesPreSignup({ verificationToken, prefs }) {

                if (IsEmpty(prefs)) {
                    return Promise.resolve();
                }

                const preferences = internals.preparePreferences(prefs);

                return http.post(`/user/preferences/pre-signup/${verificationToken}`,
                    { preferences }
                )
                    .then(() => undefined);
            }
        },
        passwordManagement: {

            requestPasswordReset({ email }) {

                return http.post('/users/request-reset', { email }).then(
                    (response) => undefined
                );
            },
            resetPassword({ resetToken, newPassword }) {

                return http.post('/users/reset-password', { resetToken, newPassword }).then(
                    (response) => response.data
                );
            },

            changePassword({ password, newPassword, newPasswordConfirm, logoutOtherDevices }) {

                return http.post('/users/change-password', { password, newPassword, newPasswordConfirm, logoutOtherDevices }, authorization()).then(
                    (response) => response.data
                );
            }
        },
        dataFetching: {
            getSchoolByVerificationToken({ token }) {

                return retryableGet(`/school/${token}`).then(
                    // eslint-disable-next-line hapi/hapi-scope-start
                    (response) => normalize(
                        fixExtendedSchool(response.data),
                        schema.ExtendedSchool
                    )
                ).catch((e) => {

                    throw e;
                });
            },
            getSchools() {

                const maybeAuthorization = userIsAuthenticated() ? authorization() : {};

                return retryableGet('/schools', maybeAuthorization).then(
                    (response) => normalize(response.data, [schema.School])
                );
            },
            async getInterests() {

                return await retryableGet('/interests').then(
                    (response) => normalize(response.data, [schema.Interest])
                );
            },
            async getCustomInterests({ schoolId }) {

                return await retryableGet(`/interests/custom/${schoolId}`).then(
                    (response) => normalize(response.data, [schema.Interest])
                );
            },
            getRoleGroups() {

                return retryableGet('/role-groups').then(
                    (response) => normalize(response.data, [schema.RoleGroup])
                );
            },
            getRoles() {

                return retryableGet(`/roles`, authorization()).then(
                    (response) => normalize(response.data, [schema.Role])
                );
            },
            getCategories() {

                return retryableGet(`/categories`).then(
                    (response) => normalize(response.data, [schema.Category])
                );
            },
            getBadges({ schoolId }) {

                return retryableGet(`schools/${schoolId}/badge-types`).then(
                    (response) => normalize(response.data, [schema.BadgeType])
                );
            },
            getConversationStarters({ schoolId }) {

                return retryableGet(`schools/${schoolId}/conversation-starters`).then(
                    (response) => normalize(response.data, [schema.ConversationStarter])
                );
            },
            getPersonalizedConversationStarters({ peerId, token }) {

                return retryableGet(`users/${peerId}/conversation-starters`, authorization(token))
                    .then((response) => response.data);
            },
            getYearsHired({ schoolId }) {

                return retryableGet(`schools/${schoolId}/years-hired`).then(
                    (response) => normalize(response.data, [schema.YearHired])
                );
            },
            getOffices({ schoolId }) {

                return retryableGet(`schools/${schoolId}/offices`).then(
                    (response) => normalize(response.data, [schema.Office])
                );
            },
            async fetchAppEntities({ entities, schoolId, managedUserId, verificationToken }) {

                const returnSchema = {
                    schools: [schema.School],
                    categories: [schema.Category],
                    roleGroups: [schema.RoleGroup],
                    badgeTypes: [schema.BadgeType],
                    conversationStarters: [schema.ConversationStarter],
                    schoolDetails: schema.ExtendedSchool,
                    yearsHired: [schema.YearHired],
                    offices: [schema.Office],
                    interests: [schema.Interest],
                    // Authentication required
                    classes: [schema.Class],
                    roles: [schema.Role]
                };

                entities.forEach((key) => {

                    if (key !== 'preferences' && !returnSchema[key]) {
                        throw new Error(`Unknown entity ${key}`);
                    }
                });

                let options = {};

                if (userIsAuthenticated()) {
                    options = {
                        ...options,
                        ...authorization()
                    };
                }

                const payload = {
                    entities,
                    schoolId,
                    managedUserId,
                    verificationToken
                };

                const response = await http.post('/app/fetch-entities', payload, retryable(options));

                const {
                    data: {
                        preferences,
                        schoolDetails,
                        ...data
                    }
                } = response;

                if (schoolDetails) {
                    data.schoolDetails = fixExtendedSchool(schoolDetails);
                }

                return {
                    preferences,
                    ...normalize(
                        data,
                        returnSchema
                    )
                };
            },
            async getUserPreferences() {

                const response = await retryableGet('/user/preferences', authorization());

                return response.data;
            },
            getUsers({ ids }) {

                const options = Object.assign({}, authorization(), {
                    params: { ids: JSON.stringify(ids) }
                });

                return retryableGet('/users', options).then(
                    (response) => normalize(response.data, [schema.User])
                );
            },
            getUserSearchResults({ page, ...payload }, pagination, isBasicSearch) {

                const paginationLimit = (pagination && pagination.limit) ? pagination.limit : 20;

                const options = Object.assign({}, authorization(), {
                    params: { page, limit: paginationLimit }
                });

                return http.post('/users/search', payload, retryable(options)).then(
                    (response) => {

                        if (response.data === true) {
                            return { result: [] };
                        }

                        // eslint-disable-next-line no-extra-boolean-cast
                        return Boolean(isBasicSearch) ?
                            normalize(response.data, [schema.User])
                            : normalize(
                                fixUserSearchResults(response.data),
                                [schema.SearchResultUser]
                            );
                    }
                );
            },
            async getUser({ id, debugMatching = false }) {

                // Problem w this method is it needlessly pulls in peersIn, peersOut and such for other users.
                const response = await retryableGet(`/users/${id}${debugMatching ? '?debugMatching=true' : ''}`, authorization());

                return normalize(fixExtendedUserWithSimilarities(response.data), schema.ExtendedUserWithSimilarities);
            },
            async getCurrentUser() {

                const options = Object.assign({}, authorization());

                const response = await retryableGet('/user', options);

                return normalize(fixCurrentUser(response.data), schema.CurrentUser);
            },
            async sync({ currentUserRevision }) {

                const response = await http.post('/sync', { currentUserRevision }, authorization());
                const { notifications, currentUserRevision: revision, currentUser } = response.data;

                return {
                    currentUserRevision: revision,
                    currentUser: currentUser ? normalize(fixCurrentUser(currentUser), schema.CurrentUser) : null,
                    notifications: {
                        notifications: normalize(notifications.notifications.map(fixNotification), [schema.Notification]),
                        otherCommunityUnreadCounts: notifications.otherCommunityUnreadCounts,
                        currentCommunityUnreadCounts: notifications.currentCommunityUnreadCounts
                    }
                };
            },
            getScheduledNotifications() {

                const options = Object.assign({}, authorization(), {});

                return retryableGet('/notifications/scheduled', options).then(
                    (response) => response.data
                );
            },
            async getNotifications() {

                const options = Object.assign({}, authorization());

                const response = await retryableGet('/notifications', options);

                const {
                    notifications,
                    otherCommunityUnreadCounts,
                    currentCommunityUnreadCounts
                } = response.data;

                return {
                    notifications: normalize(notifications.map(fixNotification), [schema.Notification]),
                    otherCommunityUnreadCounts,
                    currentCommunityUnreadCounts
                };
            },
            getGroupSearchResults({ searchText, showAll, managedUserId }) {

                if (!searchText) {
                    return Promise.resolve(normalize([], [schema.Class]));
                }

                const options = Object.assign({}, authorization(), {
                    params: { term: searchText, showAll, managedUserId }
                });

                return retryableGet('/classes/search', options).then(
                    (response) => normalize(response.data, [schema.Class])
                );
            },
            getClass({ id }) {

                const options = Object.assign({}, authorization());

                return retryableGet(`/classes/${id}`, options).then(
                    // eslint-disable-next-line hapi/hapi-scope-start
                    ({ data }) => {

                        // Set a flag that the group has been fetched
                        // This gets used in route onWillMount handlers
                        data.hasBeenFetched = true;
                        data.detailsFetchedAt = Date.now();

                        const normalized = normalize({
                            class: data,
                            users: data.users
                        }, schema.ClassWithUsers);

                        return normalized;
                    }
                );
            },
            searchClassUsers_byName({ classId, name }) {

                const options = Object.assign({}, authorization());

                return http.post(`/classes/${classId}`, { name }, options).then(
                    (response) => response.data
                );
            },
            getGroups(props) {

                const options = Object.assign({}, authorization(), {
                    params: {
                        managedUserId: props && props.managedUserId ? props.managedUserId : undefined
                    }
                });

                return retryableGet('/classes', options).then(
                    (response) => normalize(response.data, [schema.Class])
                );
            },
            getSurveys() {

                const options = Object.assign({}, authorization());

                return retryableGet('/surveys', options).then(
                    (response) => normalize(response.data, [schema.Survey])
                );
            },
            getSurvey({ id, includeUserAnswers }) {

                const options = Object.assign({}, authorization(), {
                    params: { includeUserAnswers: includeUserAnswers ? includeUserAnswers : null }
                });
                return retryableGet(`/surveys/${id}`, options).then(
                    (response) => normalize(response.data, schema.Survey)
                );
            },
            getOffers() {

                const options = Object.assign({}, authorization());

                return retryableGet('/offers', options).then(
                    (response) => normalize(response.data, [schema.Offer])
                );
            },
            getOffer({ id }) {

                return retryableGet(`/offers/${id}`, authorization()).then(
                    (response) => normalize(response.data, schema.Offer)
                );
            },
            getResources() {

                const options = Object.assign({}, authorization());

                return http.get('/resources', options).then(
                    (response) => normalize(response.data, [schema.Resource])
                );
            },
            getResource({ id }) {

                return http.get(`/resources/${id}`, authorization()).then(
                    (response) => normalize(response.data, schema.Resource)
                );
            }
        },
        offers: {
            recordImpression(offerId) {

                return http.post(`/offers/${offerId}/impression`, {}, authorization());
            },
            recordClick(offerId, source) {

                const payload = source ? { source } : {};

                return http.post(`/offers/${offerId}/click`, payload, authorization());
            },
            adminList() {

                return retryableGet('/admin/offers', authorization()).then(
                    (response) => normalize(response.data, [schema.Offer])
                );
            },
            adminCreate(payload) {

                return http.post('/admin/offers', payload, authorization()).then(
                    (response) => response.data
                );
            },
            adminUpdate(id, payload) {

                return http.patch(`/admin/offers/${id}`, payload, authorization()).then(
                    (response) => response.data
                );
            },
            adminNotificationProgress(id) {

                return retryableGet(`/admin/offers/${id}/notification-progress`, authorization()).then(
                    (response) => response.data
                );
            },
            adminDelete(id) {

                return http.delete(`/admin/offers/${id}`, authorization());
            },
            adminUploadImage(payload) {

                if (IsEmpty(payload)) {
                    return undefined;
                }

                return http.post(
                    '/admin/offers/upload-image',
                    internals.form(payload),
                    authorization()
                ).then((response) => response.data);
            },
            adminImportImageFromUrl(payload) {

                if (IsEmpty(payload)) {
                    return undefined;
                }

                return http.post('/admin/offers/image-from-url', payload, authorization()).then(
                    (response) => response.data
                );
            }
        },
        resources: {
            activateGroupLink(resourceId) {

                return http.post(`/resources/${resourceId}/activate`, {}, authorization()).then(
                    (response) => response.data
                );
            },

            recordImpression(resourceId) {

                return http.post(`/resources/${resourceId}/impression`, {}, authorization());
            },
            recordClick(resourceId, source) {

                const payload = source ? { source } : {};

                return http.post(`/resources/${resourceId}/click`, payload, authorization());
            },
            adminList() {

                return http.get('/admin/resources', authorization()).then(
                    (response) => normalize(response.data, [schema.Resource])
                );
            },
            adminCreate(payload) {

                return http.post('/admin/resources', payload, authorization()).then(
                    (response) => response.data
                );
            },
            adminUpdate(id, payload) {

                return http.patch(`/admin/resources/${id}`, payload, authorization()).then(
                    (response) => response.data
                );
            },
            adminNotificationProgress(id) {

                return retryableGet(`/admin/resources/${id}/notification-progress`, authorization()).then(
                    (response) => response.data
                );
            },
            adminDelete(id) {

                return http.delete(`/admin/resources/${id}`, authorization());
            },
            adminUploadImage(payload) {

                if (IsEmpty(payload)) {
                    return undefined;
                }

                return http.post(
                    '/admin/resources/upload-image',
                    internals.form(payload),
                    authorization()
                ).then((response) => response.data);
            },
            adminImportImageFromUrl(payload) {

                if (IsEmpty(payload)) {
                    return undefined;
                }

                return http.post('/admin/resources/image-from-url', payload, authorization()).then(
                    (response) => response.data
                );
            }
        },
        welcomePage: {
            getSettings() {

                return retryableGet('/welcome-page', authorization()).then(
                    (response) => response.data
                );
            },
            updateSchoolText(payload) {

                return http.patch('/welcome-page/school-text', payload, authorization()).then(
                    (response) => response.data
                );
            },
            updateRoleText(payload) {

                return http.patch('/welcome-page/role-text', payload, authorization()).then(
                    (response) => response.data
                );
            },
            uploadImage(payload) {

                if (IsEmpty(payload)) {
                    return undefined;
                }

                return http.post(
                    '/welcome-page/upload-image',
                    internals.form(payload),
                    authorization()
                ).then((response) => response.data);
            }
        },
        profileManagement: {
            updateProfile(userProfileDiff) {

                if (IsEmpty(userProfileDiff)) {
                    return undefined;
                }

                // Turn birthdate into ISO string

                if (userProfileDiff.birthdate) {
                    userProfileDiff.birthdate = JSON.parse(JSON.stringify(userProfileDiff.birthdate));
                }

                return http.patch(
                    '/users',
                    internals.form(userProfileDiff),
                    authorization()
                )
                    .then((response) => normalize(fixCurrentUser(response.data), schema.CurrentUser));
            },
            updateProfilePicture(userProfilePictureDiff) {

                if (IsEmpty(userProfilePictureDiff)) {
                    return undefined;
                }

                return http.patch(
                    '/users/profile-picture',
                    internals.form(userProfilePictureDiff),
                    authorization()
                ).then((response) => undefined);
            },
            updateUserPreferences(prefs) {

                if (IsEmpty(prefs)) {
                    return Promise.resolve();
                }

                const preferences = internals.preparePreferences(prefs);
                return http.post(
                    '/user/preferences',
                    { preferences },
                    authorization()
                )
                    .then(() => undefined);
            },
            async fetchUserPreferencesByVal(val) {

                const prefs = await retryableGet(`/user/preferences/${val}`);

                return prefs.data;
            },
            async updateUserPreferencesByVal(val, prefs) {

                const preferences = internals.preparePreferences(prefs);

                const response = await http.post(`/user/preferences/${val}`, { preferences });


                return response.data;
            },
            // NOTE action also registered in EXTRA_NORMALIZED_ACTIONS in src/reducers/data-fetching.js
            // PROFILE_MANAGEMENT.JOIN_SCHOOL
            async joinSchool({ schoolId }) {

                const payload = { schoolId };

                const response = await http.post('/users/join-school', payload, authorization());

                return normalize(response.data, schema.User);
            },
            // NOTE action also registered in EXTRA_NORMALIZED_ACTIONS in src/reducers/data-fetching.js
            // PROFILE_MANAGEMENT.LEAVE_SCHOOL
            async leaveSchool({ schoolId }) {

                const payload = {
                    schoolId
                };

                const response = await http.post('/users/leave-school', payload, authorization());

                return normalize(response.data, schema.User);
            },
            // NOTE action also registered in EXTRA_NORMALIZED_ACTIONS in src/reducers/data-fetching.js
            // PROFILE_MANAGEMENT.SWITCH_SCHOOL
            async switchSchool({ schoolId }) {

                const payload = {
                    schoolId
                };

                const response = await http.post(
                    '/users/switch-school',
                    payload,
                    authorization()
                );

                return response.data;
            },
            async acknowledgeNewSchoolInvites({ schoolIds }) {

                const payload = {
                    schoolIds
                };

                await http.post(
                    '/users/acknowledge-new-school-invites',
                    payload,
                    authorization()
                );

                return undefined;
            }
        },
        communication: {
            requestPeer({ userId, userIds, message, similarityText }) {

                const id = userId || userIds;

                return http.post('/users/peer', { id, message, similarityText }, authorization()).then(
                    (response) => undefined
                );
            },
            acceptPeer({ userId: id }) {

                return http.post(`/users/peer/accept/${id}`, { id }, authorization()).then(
                    (response) => undefined
                );
            },
            createTwilioConversation({ user1Id, user2Id }) {

                return http.post('/conversations/twilio/new', { user1Id: parseInt(user1Id), user2Id: parseInt(user2Id) }, authorization()).then(
                    (response) => response.data
                );
            },
            declinePeer({ userId: id }) {

                return http.post(`/users/peer/decline/${id}`, { id }, authorization()).then(
                    (response) => undefined
                );
            },
            mention({ channelSid, messageSid }) {

                return http.post('/notifications/mention', { channelSid, messageSid }, authorization()).then(
                    (response) => undefined
                );
            },
            pinMessage({ channelSid, messageSid, isPinned }) {

                return http.post('/chat/messages/pin', { channelSid, messageSid, isPinned }, authorization()).then(
                    (response) => undefined
                );
            },
            pinAnnouncementMessage({ conversationId, messageId, isPinned }) {

                return http.post('/conversations/messages/pin', { conversationId, messageId, isPinned }, authorization()).then(
                    (response) => undefined
                );
            },
            moderateAnnouncementMessage({ conversationId, messageId, status }) {

                return http.post('/conversations/messages/moderate', { conversationId, messageId, status }, authorization()).then(
                    (response) => undefined
                );
            },
            moderateMessage({ channelSid, messageSid, status }) {

                return http.post('/chat/messages/moderate', { channelSid, messageSid, status }, authorization()).then(
                    (response) => undefined
                );
            },
            removeMessage({ channelSid, messageSid }) {

                return http.post('/chat/messages/database/remove', { channelSid, messageSid }, authorization()).then(
                    (response) => undefined
                );
            },
            removeAnnouncementMessage({ conversationId, messageId }) {

                return http.post('/conversations/messages/remove', { conversationId, messageId }, authorization()).then(
                    (response) => undefined
                );
            },
            getAnnouncementConversations() {

                const options = Object.assign({}, authorization());

                return retryableGet(`/conversations`, options).then(
                    (response) => normalize(toDomain.announcementConversations(response.data), [schema.AnnouncementConversation]));
            },
            getAllChannelLastMessages() {

                const options = Object.assign({}, authorization());

                return retryableGet('/conversations/last-messages', options).then(
                    (response) => normalize(
                        internals.normalizeChannelLastMessagesResponse(response.data),
                        schema.ChannelLastMessage
                    ));
            },
            syncConversation({ channelSid, messageSid = null, action = 'updated', limit = 10 }) {

                return http.post('/conversations/sync', {
                    channelSid,
                    messageSid,
                    action,
                    limit
                }, authorization()).then(
                    (response) => response.data
                );
            },
            getAnnouncementConversation({ id }) {

                const options = Object.assign({}, authorization());

                return retryableGet(`/classes/${id}/conversation`, options).then(
                    (response) => normalize(toDomain.announcementConversations([response.data]), [schema.AnnouncementConversation]));
            },
            getAnnouncementMessages({ id }) {

                const options = Object.assign({}, authorization());

                return retryableGet(`/classes/${id}/conversation/messages`, options).then(
                    (response) => normalize(toDomain.announcementMessages(response.data), [schema.AnnouncementMessage]));
            },
            updateAnnouncementMessage({ conversationId, messageId, body }) {

                return http.post('/conversations/messages/edit', { conversationId, messageId, body }, authorization()).then(
                    (response) => undefined
                );
            },
            setAnnouncementLastReadMessageIndex({ conversationId }) {

                return http.post('/conversations/messages/read/last', { conversationId }, authorization()).then(
                    (response) => undefined
                );
            },
            setAnnouncementLastReadMessageIndex_ByConversationSid({ conversationSid, messageIndex }) {

                return http.post(`/conversations/${conversationSid}/messages/read/last`, { messageIndex }, authorization()).then(
                    (response) => undefined
                );
            },
            sendAnnouncementMessage({ classId, message }) {

                return http.post('/chat/announcement/messages/send', { classId, message }, authorization()).then(
                    // eslint-disable-next-line hapi/hapi-scope-start
                    (response) => normalize(
                        toDomain.announcementMessage(announcementMessageEntities_set(response.data)),
                        schema.AnnouncementMessage
                    )
                );
            },
            enableUser({ userId, toEnable }) {

                return http.get(`/users/${userId}/enabled/${toEnable}`, authorization()).then(
                    (response) => undefined
                );
            },
            changeUserRole({ userId, roleId }) {

                return http.post(`/users/${userId}/role`, { roleId }, authorization()).then(
                    (response) => undefined
                );
            },
            targetNotifyUser({ userId, schoolId, text, emojiSymbol, usePushSystem, startTime }) {

                return http.post(`/users/${userId}/notify`, { schoolId, text, emojiSymbol, usePushSystem, startTime }, authorization()).then(
                    (response) => undefined
                );
            },
            uploadChatImage(payload) {

                if (IsEmpty(payload)) {
                    return undefined;
                }

                return http.post(
                    '/chat/messages/upload-image',
                    internals.form(payload),
                    authorization()
                ).then((response) => {

                    return response.data;
                });
            },
            async batchTargetNotify(payload) {

                if (payload.csv) {
                    try {
                        const { csvFile, csvDuplicates } = await internals.processCsv(payload.csv);
                        payload.csv = csvFile;
                        payload.csvDuplicateCount = csvDuplicates.length;
                    }
                    catch (e) {
                        // TODO: I think we need to return instead of throw because of
                        // how actions are handled, but need to make sure.
                        return e;
                    }
                }

                return http.postForm('/notifications/targeted-batch',
                    internals.form(payload),
                    authorization()
                ).then(
                    (response) => response.data
                );
            },
            async uploadPreapproved({ roleName, csv, userByText, schoolId, notifyUsers }) {

                let totalRows = 0;
                let csvFile = csv;
                let csvDuplicates = [];

                // Priority given to the typed-in field
                if (userByText) {
                    totalRows = 1;
                    csvFile = new File(
                        [`email\n${userByText}\n`],
                        'preapproved-single-user-upload.csv', {
                        type: 'image/jpeg',
                        lastModified: new Date()
                    }
                    );
                }
                else if (csv) {
                    try {
                        const processedResults = await internals.processCsv(csv);

                        csvFile = processedResults.csvFile;
                        totalRows = processedResults.totalRows;
                        csvDuplicates = processedResults.csvDuplicates;
                    }
                    catch (e) {
                        // TODO: I think we need to return instead of throw because of
                        // how actions are handled, but need to make sure.
                        return e;
                    }
                }

                const payload = {
                    csv: csvFile,
                    totalRowsCount: totalRows,
                    roleName,
                    notifyUsers
                };

                const httpPost = http.postForm(`/schools/${schoolId}/admin/preapproved`,
                    internals.form(payload),
                    authorization()
                );

                return {
                    httpPost,
                    csvDuplicates,
                    totalRows
                };
            },
            async checkPreapprovedProgress(processingCacheKey) {

                return await retryableGet(`/schools/admin/preapproved/check-progress/${processingCacheKey}`, authorization());
            },
            addTwilioUserToChannel({ userId, channelSid }) {

                return http.post('/chat/twilio/add-user', { userId, channelSid }, authorization()).then(
                    (response) => response.data
                );
            },
            removeTwilioUserFromChannel({ userId, channelSid }) {

                return http.post('/chat/twilio/remove-user', { userId, channelSid }, authorization()).then(
                    (response) => undefined
                );
            },
            addReaction({ messageSid, channelSid, reaction, userId, classId } = {}) {

                const payload = {
                    messageSid,
                    channelSid,
                    reaction,
                    userId,
                    classId
                };

                return http.post('/reactions/add', payload, authorization()).then(
                    (response) => response.data
                );
            },
            removeReaction({ messageSid, channelSid, reaction, userId } = {}) {

                const payload = {
                    messageSid,
                    channelSid,
                    reaction,
                    userId
                };

                return http.post('/reactions/remove', payload, authorization()).then(
                    (response) => response.data
                );
            }
        },
        appFeedback: {
            submitBugReport({ bugType, bugDetails, suggestedInterestCategory, email }) {

                let auth = null;

                try {
                    auth = authorization();
                }
                catch (ignoreErr) { }

                const appVersion = process.env.APP_VERSION;

                return http.post('/bug-report', { bugType, bugDetails, suggestedInterestCategory, email, appVersion }, auth).then(
                    (response) => undefined
                );
            },
            updateReviewStatus({ status }) {

                return http.post('/users/review', { status }, authorization()).then(
                    (response) => undefined
                );
            },
            fetchUserReviewAvailableStatus() {

                return http.post('/users/review/status', {}, retryable(authorization())).then(
                    (response) => response
                );
            }
        },
        notifications: {
            markRead({ ids }) {

                return http.post('/notifications/read', { ids, read: true }, authorization()).then(
                    (response) => undefined
                );
            },
            acceptTransfer({ notificationId }) {

                return http.get(`/users/transfer/accept/${notificationId}`, authorization()).then(
                    (response) => undefined
                );
            },
            clearMentions({ channelSid }) {

                const options = {
                    ...authorization(),
                    params: { channelSid }
                };

                return http.delete('/notifications/mention', options).then((response) => ({ ids: response.data }));
            },
            accept({ type, userId: id }) {

                return http.post(`/users/peer/accept/${id}`, { id }, authorization()).then(
                    (response) => undefined
                );
            },
            decline({ type, userId: id }) { // TODO dupe of declinePeer()

                assert(type === 'peer');

                return http.post(`/users/${type}/decline/${id}`, { id }, authorization()).then(
                    (response) => undefined
                );
            },
            answerQuestion({ notificationId: id, answer }) {

                return http.post('/notifications/answer-question', { id, answer }, authorization()).then(
                    (response) => undefined
                );
            },
            dismiss({ id, ids }) {

                ids = ids || [id];

                return http.post('/notifications/dismiss', { ids }, authorization()).then(
                    (response) => undefined
                );
            },
            delete({ notificationId }) {

                return http.delete(`/notifications/scheduled/${notificationId}`, authorization()).then(
                    (response) => undefined
                );
            }
        },
        classes: {
            join({ id, userId }) {
                // userId is typically only set when a superuser is performing actions
                return http.post(`/classes/join/${id}`, { userId }, authorization()).then(
                    (response) => normalize(fixCurrentUser(response.data), schema.CurrentUser)
                );
            },
            leave({ id, userId }) {
                // userId is typically only set when a superuser is performing actions
                return http.post(`/classes/leave/${id}`, { userId }, authorization()).then(
                    (response) => normalize(fixCurrentUser(response.data), schema.CurrentUser)
                );
            },
            create(class_) {

                return http.post('/classes', class_, authorization()).then(
                    (response) => normalize(response.data, schema.Class)
                );
            },
            update(class_) {

                return http.patch('/classes', class_, authorization()).then(
                    (response) => normalize(response.data, schema.Class)
                );
            },
            updateNotificationLevel({ id, notificationLevel }) {

                return http.post(`/classes/${id}/update-notification-level`, {
                    notificationLevel
                }, authorization()).then(
                    (response) => undefined
                );
            }
        },
        surveys: {
            async create(payload) {

                if (payload.csv) {
                    try {
                        const { csvFile } = await internals.processCsv(payload.csv);
                        payload.csv = csvFile;
                    }
                    catch (e) {
                        // TODO: I think we need to return instead of throw because of
                        // how actions are handled, but need to make sure.
                        return e;
                    }
                }

                return http.postForm('/surveys',
                    internals.form(payload),
                    authorization()
                ).then(
                    (response) => normalize(response.data, schema.Survey)
                );
            },
            invite(surveyId, inviteData) {

                return http.post(
                    `/surveys/${surveyId}/invite`,
                    {
                        selectedRole: inviteData.selectedRole,
                        selectedUsers: inviteData.selectedUsers,
                        schoolId: inviteData.schoolId
                    },
                    authorization()
                ).then((response) => response.data);
            },
            update(survey_) {

                return http.patch(`/surveys/${survey_.id}`, survey_, authorization()).then(
                    (response) => normalize(response.data, schema.Survey)
                );
            },
            startSolving({ userId, surveyId }) {

                return http.post(`/surveys/${surveyId}/start`, { userId }, authorization()).then(
                    (response) => response.data
                );
            },
            submitAnswers({ questions, userId, surveyId }) {

                return http.post(`/surveys/${surveyId}/answers/submit`, { questions, userId }, authorization()).then(
                    (response) => response.data
                );
            },
            report({ surveyId }) {

                return retryableGet(`/surveys/${surveyId}/report`, authorization()).then(
                    (response) => response.data
                );
            },
            delete({ surveyId }) {

                return http.delete(`/surveys/${surveyId}`, authorization()).then(
                    (response) => response.data
                );
            },
            stopSurvey({ surveyId }) {

                return http.post(`/surveys/${surveyId}/stop`, { surveyId }, authorization()).then(
                    (response) => response.data
                );
            }
        },
        dashboard: {
            getUserStats(filters = {}) {

                const { startDate, endDate, incomingClass, gender, transfer, roleIds, schoolId } = filters;

                const params = {
                    startDate: startDate?.toISOString(),
                    endDate: endDate?.toISOString(),
                    incomingClass,
                    gender,
                    transfer,
                    roleIds: roleIds?.length ? roleIds.join(',') : undefined,
                    schoolId
                };

                const options = Object.assign({}, authorization(), { params });

                return retryableGet('/dashboard/users/stats', options).then(
                    (response) => response.data
                );
            },
            getMessagesStats({ startDate, endDate, schoolId } = {}) {

                const options = Object.assign({}, authorization(), {
                    params: {
                        startDate: startDate?.toISOString(),
                        endDate: endDate?.toISOString(),
                        schoolId
                    }
                });

                return retryableGet('/dashboard/messages/stats', options).then(
                    (response) => response.data
                );
            }
        }
    };
};

internals.form = (values) => {

    const formData = new FormData();

    for (const key in values) {
        let value = values[key];

        if (value && typeof value === 'object' && !(value instanceof Blob)) {
            if (Moment.isMoment(value)) {
                value = value.toISOString();
            }
            else {
                value = JSON.stringify(value);
            }
        }

        formData.append(key, value);
    }

    return formData;
};

internals.preparePreferences = (prefs) => {

    const validPrefs = {
        sms: normalizeSmsPreference,
        connectionsVisibility: true,
        groupVisibility: true,
        groupMsgNotifications: true,
        digestInterval: true,
        pendingConnections: true,
        careerAlerts: true,
        hideInterestPopup: true,
        sortGroupsBy: true,
        sortMessagesBy: true,
        messageNotifications: true
    };

    const isValidPref = (key) => !!validPrefs[key];

    const castValues = (collector, key) => ({
        ...collector,
        [key]: (typeof validPrefs[key] === 'function') ? validPrefs[key](prefs[key]) : prefs[key]
    });

    return Object.keys(prefs).filter(isValidPref).reduce(castValues, {});
};

internals.formatCsvHeaders = (csvHeaderArr) => {

    return csvHeaderArr.map((header) => {

        const headerTitle = header.toLowerCase().trim();

        // Change lowercase to camelCase for header columns
        switch (headerTitle) {
            case 'firstname':
                return 'firstName';
            case 'lastname':
                return 'lastName';
            case 'onlinestudent':
                return 'onlineStudent';
            case 'opentosocial':
                return 'openToSocial';
            case 'serialnumber':
                return 'serialNumber';
            case 'phonecountry':
                return 'phoneCountry';
            case 'email':
            case 'phone':
            case 'badge':
            case 'career':
            case 'bio':
            case 'title':
            case 'parent':
                return headerTitle;
            default:
                return headerTitle;
        }
    });
};

internals.processCsv = async (csv) => {

    let totalRows = 0;
    const csvDuplicates = [];

    const processCsv = (csvString) => {

        // Should use a lib for this but eh, this works
        csvString = csvString.replace(/(\r\n|\r)/gm, '\n');
        const csvHeaderStr = csvString.slice(0, csvString.indexOf('\n'));
        const csvHeaderArray = csvHeaderStr.split(',');
        const csvRows = csvString.slice(csvString.indexOf('\n') + 1).split('\n');

        const formattedCsvHeaders = internals.formatCsvHeaders(csvHeaderArray);

        // Dedupe the csv by email.
        // Ensure no duplicates in the CSV are sent up to the server
        const rowsByEmail = {};

        csvRows.forEach((row) => {

            // Strip newlines and non-ASCII characters from the row
            const sanitizedRow = row.replace(/(\r\n|\n|\r)/gm, '').replace(/[^\x20-\x7E]/g, '');

            if (!sanitizedRow.trim()) {
                // Skip empty rows
                return;
            }

            const valuesArr = sanitizedRow.split(',');

            const rowObj = formattedCsvHeaders.reduce((collector, header, index) => {

                let item = valuesArr[index];

                if (item) {
                    // Strip newlines and non-ASCII characters from individual items
                    item = item.replace(/(\r\n|\n|\r)/gm, '').replace(/[^\x20-\x7E]/g, '');
                    collector[header] = item;
                }
                else {
                    collector[header] = item;
                }

                if ((header === 'email' || header === 'emails') && typeof item === 'string') {
                    collector.email = item.toLowerCase();
                }

                return collector;
            }, {});

            if (!rowObj.email || !IsEmail(rowObj.email)) {
                // Skip rows without a valid email
                return;
            }
            else if (rowsByEmail[rowObj.email]) {
                csvDuplicates.push(rowObj.email);
            }
            else {
                // First time adding to rowsByEmail
                totalRows += 1;
            }

            // Overwrite earlier row if it existed to ensure no CSV-level duplicates
            rowsByEmail[rowObj.email] = rowObj;
        });

        return Object.values(rowsByEmail)
            .reduce((processedCsv, rowObj) => {

                let row = '';
                formattedCsvHeaders.forEach((header, i) => {

                    if (i === 0) {
                        row += rowObj[header];
                    }
                    else {
                        row += `,${rowObj[header] || ''}`;
                    }
                });

                return processedCsv + `\n${row}`;
            }, formattedCsvHeaders.join(',')); // Start with header row
    };

    const csvFile = await new Promise((resolve, reject) => {

        try {
            const fileReader = new FileReader();

            fileReader.onload = (event) => {

                const processed = processCsv(event.target.result);

                return resolve(new File(
                    [processed],
                    csv.name,
                    {
                        type: 'text/csv',
                        lastModified: new Date()
                    }
                ));
            };

            fileReader.onerror = reject;

            fileReader.readAsText(csv);
        }
        catch (err) {
            reject(err);
        }
    });

    return {
        totalRows,
        csvFile,
        csvDuplicates
    };
};

internals.isPendingMfaStatus = (status) => AuthConstants.authFlow.pendingMfaStatuses.includes(status);

internals.normalizeChannelLastMessagePreview = (message = {}) => {

    if (!message) {
        return null;
    }

    const source = message.lastMessage || message.message || message;

    return {
        sid: source.sid || source.messageSid || source.twilioMessageSid || source.id || null,
        index: typeof source.index === 'number' ? source.index
            : typeof source.messageIndex === 'number' ? source.messageIndex
                : null,
        author: source.author || source.authorId || source.userId || source.senderId || null,
        body: source.body || source.messageBody || source.message || source.text || source.content || '',
        timestamp: source.timestamp || source.dateCreated || source.createdAt || source.updatedAt || null
    };
};

internals.normalizeChannelLastMessagesResponse = (data) => {

    if (!data) {
        return {};
    }

    const toEntry = (channelSid, value) => [
        channelSid,
        internals.normalizeChannelLastMessagePreview(value)
    ];

    if (Array.isArray(data)) {
        return data.reduce((collector, item) => {

            const channelSid = item?.channelSid || item?.channel || item?.conversationSid || item?.conversation?.sid;

            if (!channelSid) {
                return collector;
            }

            const [key, value] = toEntry(channelSid, item);
            collector[key] = value;

            return collector;
        }, {});
    }

    return Object.entries(data).reduce((collector, [channelSid, value]) => {

        collector[channelSid] = internals.normalizeChannelLastMessagePreview(value);

        return collector;
    }, {});
};


internals.buildSignupContext = (responseData = {}, { fallbackEmail = null } = {}) => {

    const {
        signupContext = {},
        verificationToken,
        schoolId,
        roleId,
        email
    } = responseData;

    const usingSSO = signupContext.usingSSO === true || responseData.usingSSO === true;
    const usingPassword = signupContext.usingPassword === true || responseData.usingPassword === true;

    return {
        verificationToken: signupContext.verificationToken || verificationToken,
        schoolId: signupContext.schoolId || schoolId,
        roleId: signupContext.roleId || roleId,
        email: signupContext.email || email || fallbackEmail,
        sso: signupContext.sso,
        authMethod: signupContext.authMethod
            || (usingSSO ? AuthConstants.authFlow.methods.sso : undefined)
            || (usingPassword ? AuthConstants.authFlow.methods.password : undefined),
        usingSSO,
        usingPassword,
        mfaMethod: signupContext.mfaMethod || signupContext.mfa,
        hasPassword: signupContext.hasPassword,
        hasSSOToken: signupContext.hasSSOToken,
        isEmailVerified: signupContext.isEmailVerified
    };
};

internals.buildCompletedSignupAuthResult = (responseData) => {

    const result = responseData && typeof responseData === 'object'
        ? responseData
        : { email: responseData };
    const tokens = result.tokens || internals.buildAuthTokens(result);

    return {
        ...result,
        tokens
    };
};

internals.buildVerificationTokenAuthResult = (responseData = {}) => internals.isPendingMfaStatus(responseData.status)
    ? responseData
    : ({
        tokens: {
            nearpeer: responseData.nearpeer,
            twilio: responseData.twilio
        },
        isVerified: responseData.isVerified,
        schoolId: responseData.schoolId
    });

internals.buildAuthTokens = (result = {}) => {

    const tokens = ['nearpeer', 'twilio', 'sso'].reduce((collector, key) => {

        if (result[key]) {
            collector[key] = result[key];
        }

        return collector;
    }, {});

    if (!Object.keys(tokens).length) {
        return undefined;
    }

    return tokens;
};

internals.getSchoolCheckUrl = ({ email, schoolId } = {}) => {

    const query = new URLSearchParams({ email });

    if (schoolId) {
        query.set('schoolId', schoolId);
    }

    return `/login/school-check?${query.toString()}`;
};
